// src/components/dubbing/VideoPreviewModal.js
import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { COLORS } from '../../utils/color';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    background: COLORS.gradients.background,
    borderRadius: '24px',
    maxWidth: '90vw',
    maxHeight: '90vh',
    margin: theme.spacing(2),
    overflow: 'hidden',
  },
}));

const VideoContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  backgroundColor: '#000',
  borderRadius: '16px',
  overflow: 'hidden',
}));

const VideoElement = styled('video')({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
});

const ControlsOverlay = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'visible',
})(({ theme, visible }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  opacity: visible ? 1 : 0,
  transition: 'opacity 0.3s ease',
  '&:hover': {
    opacity: 1,
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  color: 'white',
  zIndex: 10,
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
  },
}));

const ProgressBar = styled(Box)(({ theme }) => ({
  flex: 1,
  height: '4px',
  backgroundColor: 'rgba(255, 255, 255, 0.3)',
  borderRadius: '2px',
  cursor: 'pointer',
  position: 'relative',
}));

const ProgressFill = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'progress',
})(({ theme, progress }) => ({
  height: '100%',
  backgroundColor: COLORS.primary.main,
  borderRadius: '2px',
  width: `${progress}%`,
  transition: 'width 0.1s ease',
}));

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  zIndex: 5,
}));

const VideoPreviewModal = ({ open, onClose, job }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  
  const videoRef = useRef(null);
  const hideControlsTimeoutRef = useRef(null);

  // Hide controls after 3 seconds of no mouse movement
  const handleMouseMove = () => {
    setControlsVisible(true);
    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
    }
    hideControlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setControlsVisible(false);
      }
    }, 3000);
  };

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleMuteToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if (videoRef.current.webkitRequestFullscreen) {
        videoRef.current.webkitRequestFullscreen();
      } else if (videoRef.current.msRequestFullscreen) {
        videoRef.current.msRequestFullscreen();
      }
    }
  };

  const handleProgressClick = (e) => {
    if (videoRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const percentage = clickX / width;
      const newTime = percentage * videoRef.current.duration;
      videoRef.current.currentTime = newTime;
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const currentProgress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setProgress(currentProgress);
    }
  };

  const handleVideoLoad = () => {
    setLoading(false);
    setError(false);
  };

  const handleVideoError = () => {
    setLoading(false);
    setError(true);
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleClose = () => {
    // Pause video when closing
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setIsPlaying(false);
    setProgress(0);
    setLoading(true);
    setError(false);
    onClose();
  };

  if (!job || !job.video_url) {
    return null;
  }

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
    >
      <DialogContent sx={{ p: 0, position: 'relative', height: '80vh' }}>
        <CloseButton onClick={handleClose}>
          <CloseIcon />
        </CloseButton>

        <VideoContainer onMouseMove={handleMouseMove}>
          {loading && (
            <LoadingOverlay>
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress 
                  size={60} 
                  sx={{ color: COLORS.primary.main, mb: 2 }} 
                />
                <Typography variant="body1" sx={{ color: 'white' }}>
                  Loading video...
                </Typography>
              </Box>
            </LoadingOverlay>
          )}

          {error && (
            <LoadingOverlay>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: COLORS.status.error, mb: 1 }}>
                  Failed to load video
                </Typography>
                <Typography variant="body2" sx={{ color: 'white' }}>
                  Please try again later
                </Typography>
              </Box>
            </LoadingOverlay>
          )}

          <VideoElement
            ref={videoRef}
            src={job.video_url}
            onTimeUpdate={handleTimeUpdate}
            onLoadedData={handleVideoLoad}
            onError={handleVideoError}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onClick={handlePlayPause}
          />

          <ControlsOverlay visible={controlsVisible || !isPlaying}>
            <IconButton 
              onClick={handlePlayPause}
              sx={{ color: 'white', p: 1 }}
            >
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>

            <Typography variant="body2" sx={{ color: 'white', minWidth: '45px' }}>
              {videoRef.current ? formatTime(videoRef.current.currentTime) : '0:00'}
            </Typography>

            <ProgressBar onClick={handleProgressClick}>
              <ProgressFill progress={progress} />
            </ProgressBar>

            <Typography variant="body2" sx={{ color: 'white', minWidth: '45px' }}>
              {videoRef.current ? formatTime(videoRef.current.duration) : '0:00'}
            </Typography>

            <IconButton 
              onClick={handleMuteToggle}
              sx={{ color: 'white', p: 1 }}
            >
              {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
            </IconButton>

            <IconButton 
              onClick={handleFullscreen}
              sx={{ color: 'white', p: 1 }}
            >
              <FullscreenIcon />
            </IconButton>
          </ControlsOverlay>
        </VideoContainer>

        {/* Video Info */}
        <Box sx={{ 
          position: 'absolute', 
          top: 16, 
          left: 16, 
          background: 'rgba(0, 0, 0, 0.6)',
          borderRadius: '8px',
          padding: 1,
          maxWidth: '60%',
        }}>
          <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>
            {job.project_name || job.video_filename || 'Dubbed Video'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            {job.source_language?.toUpperCase()} → {job.target_language?.toUpperCase()}
          </Typography>
        </Box>
      </DialogContent>
    </StyledDialog>
  );
};

export default VideoPreviewModal;